import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { Lock, Mail, User, Music2, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

/**
 * Customer-facing sign-in and account-creation page.
 * Route: /login   (default: sign-in tab)
 * Route: /register (default: create-account tab)
 * Query: ?redirect=/some/path  (returns the user there on success)
 */
export const CustomerLogin = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, register, isAuthenticated } = useAuth();

  const initialMode = location.pathname === '/register' ? 'register' : 'signin';
  const [mode, setMode] = useState(initialMode);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);

  const redirectTo = searchParams.get('redirect') || '/';

  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, navigate, redirectTo]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate(redirectTo, { replace: true });
    } catch (err) {
      toast.error(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await register({ email, password, first_name: firstName, last_name: lastName });
      toast.success('Account created — welcome to Casa Samba!');
      navigate(redirectTo, { replace: true });
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 relative overflow-hidden">
      {/* subtle carnival glow behind card */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-fuchsia-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-amber-500/20 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
        data-testid="customer-login-card"
      >
        <div className="bg-card/70 backdrop-blur-md border border-border/50 rounded-3xl p-8 shadow-2xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-glow mb-4">
              <Music2 className="w-10 h-10 text-black" />
            </div>
            <h1 className="text-3xl font-bold font-display text-foreground mb-1">
              Escola de Samba Casa Samba
            </h1>
            <p className="text-muted-foreground text-sm">
              {mode === 'signin' ? 'Welcome back — sign in to continue' : 'Create your account to get started'}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex bg-background/60 rounded-full p-1 mb-6" data-testid="login-tabs">
            <button
              type="button"
              onClick={() => setMode('signin')}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all ${
                mode === 'signin'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-signin"
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => setMode('register')}
              className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all ${
                mode === 'register'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-register"
            >
              Create Account
            </button>
          </div>

          <AnimatePresence mode="wait">
            {mode === 'signin' ? (
              <motion.form
                key="signin"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
                transition={{ duration: 0.2 }}
                onSubmit={handleSignIn}
                className="space-y-5"
                data-testid="signin-form"
              >
                <Field
                  icon={Mail}
                  type="email"
                  label="Email"
                  value={email}
                  onChange={setEmail}
                  placeholder="you@example.com"
                  testId="signin-email"
                />
                <Field
                  icon={Lock}
                  type="password"
                  label="Password"
                  value={password}
                  onChange={setPassword}
                  placeholder="••••••••"
                  testId="signin-password"
                />
                <SubmitButton loading={loading} label="Sign In" testId="signin-submit" />
              </motion.form>
            ) : (
              <motion.form
                key="register"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.2 }}
                onSubmit={handleRegister}
                className="space-y-5"
                data-testid="register-form"
              >
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    icon={User}
                    type="text"
                    label="First name"
                    value={firstName}
                    onChange={setFirstName}
                    placeholder="Ana"
                    testId="register-first-name"
                  />
                  <Field
                    icon={User}
                    type="text"
                    label="Last name"
                    value={lastName}
                    onChange={setLastName}
                    placeholder="Silva"
                    testId="register-last-name"
                  />
                </div>
                <Field
                  icon={Mail}
                  type="email"
                  label="Email"
                  value={email}
                  onChange={setEmail}
                  placeholder="you@example.com"
                  testId="register-email"
                />
                <Field
                  icon={Lock}
                  type="password"
                  label="Password"
                  value={password}
                  onChange={setPassword}
                  placeholder="Minimum 6 characters"
                  testId="register-password"
                  minLength={6}
                />
                <SubmitButton loading={loading} label="Create Account" testId="register-submit" />
                <p className="text-xs text-muted-foreground text-center">
                  By creating an account you agree to the {' '}
                  <Link to="/terms-of-service" className="text-primary hover:underline">Terms</Link>
                  {' '} and {' '}
                  <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </div>

        {/* Footer nav */}
        <div className="text-center mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm"
            data-testid="back-to-store-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Store
          </button>
          <span className="hidden sm:inline text-muted-foreground/40">·</span>
          <Link
            to="/admin"
            className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
            data-testid="admin-login-link"
          >
            Staff login
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

// Small helpers to keep the form JSX readable.

const Field = ({ icon: Icon, type, label, value, onChange, placeholder, testId, minLength }) => (
  <div>
    <label className="block text-sm font-bold text-foreground mb-2">{label}</label>
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        minLength={minLength}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full pl-12 pr-4 py-3 bg-background/70 border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
      />
    </div>
  </div>
);

const SubmitButton = ({ loading, label, testId }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testId}
    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold text-lg shadow-glow transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
  >
    {loading ? (
      <>
        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
        Please wait…
      </>
    ) : (
      <>
        <Lock className="w-5 h-5" />
        {label}
      </>
    )}
  </button>
);

export default CustomerLogin;
